export const CONDITION_BOARD_EDGE = 7.05;
export const CONDITION_DIE_FLOOR = -0.815;

export const BOARD_CONDITIONS = {
  crosswind: {
    name: 'Crosswind',
    color: '#74eaff',
    description: 'A steady current bends your throw sideways.',
    tip: 'Aim into the wind or ride it toward a reward.',
  },
  magnetic: {
    name: 'Magnetic Field',
    color: '#ff9bd8',
    description: 'An edge magnet curves the moving die toward it.',
    tip: 'Use its pull to curve a shot; more power helps you pass it.',
  },
  ice: {
    name: 'Ice Lanes',
    color: '#b9f6ff',
    description: 'The frosted lane is slippery. Expect a longer slide.',
    tip: 'Aim for solid ground or use the ice to reach farther cells.',
  },
  lowGravity: {
    name: 'Low Gravity',
    color: '#c5a8ff',
    description: 'Slower falls give throws and bumps more airtime.',
    tip: 'Try a softer throw and save a bump for the landing.',
  },
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export const normalizeBoardCondition = (condition) => {
  if (!condition || !Object.keys(BOARD_CONDITIONS).includes(condition.type))
    return null;
  return {
    type: condition.type,
    direction: condition.direction === -1 ? -1 : 1,
    strength: Number.isFinite(condition.strength)
      ? clamp(condition.strength, 0.65, 1.15)
      : 1,
    laneAxis: condition.laneAxis === 'column' ? 'column' : 'row',
    lane: condition.lane === 1 || condition.lane === 3 ? condition.lane : 2,
  };
};

const hash = (text) => {
  let value = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    value = Math.imul(value ^ text.charCodeAt(index), 16777619);
  }
  return value >>> 0;
};

// Door conditions are derived from a saved run seed, so inspecting a door,
// backing out, re-rendering, or reloading cannot reroll its environment.
export const getRoomBoardCondition = (game, roomId) => {
  if (!roomId || roomId === 'boss' || game.floor === 1) return null;
  const value = hash(`${game.conditionSeed ?? 0}:${game.floor}:${roomId}`);
  const types = Object.keys(BOARD_CONDITIONS);
  return normalizeBoardCondition({
    type: types[value % types.length],
    direction: value & 256 ? -1 : 1,
    strength: game.floor === 2 ? 0.85 : 1,
    laneAxis: value & 512 ? 'column' : 'row',
    lane: value & 1024 ? 1 : 3,
  });
};

export const getBoardConditionName = (condition) => {
  const definition = BOARD_CONDITIONS[condition?.type];
  if (!definition) return 'Stable board';
  if (condition.type === 'crosswind')
    return `${definition.name} ${condition.direction === -1 ? '←' : '→'}`;
  if (condition.type === 'magnetic')
    return `${definition.name} · ${
      condition.direction === -1 ? 'left' : 'right'
    } edge`;
  return definition.name;
};

export const getIceTileIndices = (condition) =>
  condition?.type === 'ice'
    ? Array.from({ length: 5 }, (_, index) =>
        condition.laneAxis === 'column'
          ? index * 5 + condition.lane
          : condition.lane * 5 + index
      )
    : [];

const isOnIce = (condition, position) => {
  if (condition?.type !== 'ice') return false;
  const coordinate = condition.laneAxis === 'column' ? position.x : position.z;
  return Math.abs(coordinate - (condition.lane - 2) * 2.85) <= 1.35;
};

export const getConditionGravity = (condition) =>
  condition?.type === 'lowGravity' ? 6.1 : 9.8;
export const getConditionFriction = (condition, position) =>
  isOnIce(condition, position) ? 0.14 : 1;

export const getMagnetPosition = (condition) => ({
  x: (condition?.direction === -1 ? -1 : 1) * 7.65,
  z: -0.8,
});

export const getConditionAcceleration = (
  condition,
  position,
  { onGround = false, speed = 2 } = {}
) => {
  const settle = onGround ? clamp(speed / 1.2, 0, 1) * 0.6 : 1;
  const strength = (condition?.strength || 1) * settle;
  if (strength === 0) return { x: 0, z: 0 };
  if (condition?.type === 'crosswind')
    return { x: condition.direction * 2.7 * strength, z: 0 };
  if (condition?.type === 'magnetic') {
    const magnet = getMagnetPosition(condition);
    const dx = magnet.x - position.x;
    const dz = magnet.z - position.z;
    const distance = Math.hypot(dx, dz);
    if (distance < 0.01) return { x: 0, z: 0 };
    const force = (4.6 * strength) / (1 + (distance * distance) / 30);
    return { x: (dx / distance) * force, z: (dz / distance) * force };
  }
  return { x: 0, z: 0 };
};

export const startConditionThrow = (physics, condition) => {
  physics.boardCondition = normalizeBoardCondition(condition);
};

export const applyBoardConditionStep = (physics, position, step) => {
  if (
    !physics.boardCondition ||
    !physics.active ||
    physics.completed ||
    physics.grounding ||
    physics.grounded
  )
    return;
  const condition = physics.boardCondition;
  const acceleration = getConditionAcceleration(condition, position, {
    onGround: physics.onGround,
    speed: Math.hypot(physics.velocity.x, physics.velocity.z),
  });
  physics.velocity.x += acceleration.x * step;
  physics.velocity.z += acceleration.z * step;
};
